import type React from 'react';

import type { ExtendedRouteObject } from './types';

/**
 * Small util used to wrap import thunks for the route definitions. ReactRouter expects
 * lazy routes to return a module exposing keys matching a route definition, hence we map
 * the name of a component export to `Component`.
 *
 * @see https://reactrouter.com/6.30.0/route/route#lazy
 */
export function C<T, N extends keyof T>(thunk: Promise<T>, name: N): Promise<{ Component: T[N] }> {
  return thunk.then((result) => ({
    Component: result[name],
  }));
}

/**
 * Small util used to wrap import hunks for the route definitions. Uses the C function to expose the
 * default export as `Component`.
 */
export function CD<T extends { default: React.ComponentType }>(
  thunk: Promise<T>,
): Promise<{ Component: T['default'] }> {
  return C(thunk, 'default');
}

const getRoutePath = (route: ExtendedRouteObject, parentPath: string): string => {
  if (route.path) return `${parentPath.replace(/\/$/, '')}${route.path.replace(/\/$/, '')}`;
  return parentPath;
};

export const mapPathToHandle = (routes: ExtendedRouteObject[]): ExtendedRouteObject[] => {
  const mapper = (mappingRoutes: ExtendedRouteObject[], parentPath: string) =>
    mappingRoutes.map((route): ExtendedRouteObject => {
      const routePath = getRoutePath(route, parentPath);
      return {
        ...route,
        handle: {
          ...route.handle,
          routePath,
        },
        children: route.children ? mapper(route.children, routePath) : route.children,
      } as ExtendedRouteObject;
    });

  return mapper(routes, '/');
};
